import { Controller } from "@hotwired/stimulus"

// The search page's Company box, as a combobox: as the rep types, the Sites
// CONEK already holds by that name drop down under it, and the last row is
// always "Search for new sites" — the form's own search
// (Prospecting::SiteSuggestionsController renders the list into a Turbo
// Frame). A building we already have is found before it is prospected again.
//
// The box keeps the focus throughout, as a combobox's does: the arrow keys
// move `aria-selected` through the options, Enter acts on the selected one,
// and Enter with none selected is the ordinary submit. Choosing a Site
// searches for that Site alone (its `data-href`, `?account=`): the map shows
// it, selected, with the lines to its other locations, and nothing else.

// Below this nothing is asked; the server holds the same minimum
// (SiteSuggestionsController::MIN_QUERY_LENGTH).
const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

export default class extends Controller {
  static targets = ["input", "panel", "frame", "option"]
  static values = { url: String }

  disconnect() {
    clearTimeout(this._timer)
  }

  typed() {
    clearTimeout(this._timer)
    const term = this.inputTarget.value.trim()

    if (term.length < MIN_QUERY_LENGTH) {
      this._term = null
      this.close()
      this.frameTarget.removeAttribute("src")
      this.frameTarget.replaceChildren()
      return
    }

    this._timer = setTimeout(() => this._ask(term), DEBOUNCE_MS)
  }

  // The frame answered. Opened only while the rep is still in the box: a
  // late answer must not drop a list over whatever they moved on to.
  loaded() {
    this._activeIndex = -1
    if (
      this.optionTargets.length &&
      document.activeElement === this.inputTarget
    ) {
      this.open()
    } else {
      this.close()
    }
  }

  reopen() {
    if (
      this._term &&
      this._term === this.inputTarget.value.trim() &&
      this.optionTargets.length
    ) {
      this.open()
    }
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        if (!this.optionTargets.length) return
        event.preventDefault()
        if (this.panelTarget.hidden) this.open()
        const step = event.key === "ArrowDown" ? 1 : -1
        const count = this.optionTargets.length
        const from = this._activeIndex ?? -1
        this._select(
          from < 0 && step < 0 ? count - 1 : (from + step + count) % count,
        )
        break
      }
      case "Enter": {
        const option = this.panelTarget.hidden
          ? null
          : this.optionTargets[this._activeIndex ?? -1]
        if (!option) return // the form's own submit: a search for new sites
        event.preventDefault()
        this._choose(option)
        break
      }
      case "Escape":
        if (this.panelTarget.hidden) return
        event.preventDefault()
        this.close()
        break
      case "Tab":
        this.close()
        break
    }
  }

  pick(event) {
    this._choose(event.currentTarget)
  }

  dismiss(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  open() {
    this.panelTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.panelTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this._select(-1)
  }

  _ask(term) {
    if (term === this._term) return this.reopen()

    this._term = term
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", term)
    this.frameTarget.setAttribute("src", `${url.pathname}${url.search}`)
  }

  _select(index) {
    this._activeIndex = index
    this.optionTargets.forEach((option, i) => {
      option.setAttribute("aria-selected", i === index ? "true" : "false")
    })
    const active = this.optionTargets[index]
    if (active) {
      this.inputTarget.setAttribute("aria-activedescendant", active.id)
      active.scrollIntoView?.({ block: "nearest" })
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  _choose(option) {
    this.close()

    if (option.dataset.searchNew) {
      this.inputTarget.form?.requestSubmit()
      return
    }

    if (!option.dataset.href) return
    // A Turbo visit when Turbo is on the page, as the search form's own
    // submit is; a plain one otherwise.
    if (window.Turbo?.visit) {
      window.Turbo.visit(option.dataset.href)
    } else {
      window.location.assign(option.dataset.href)
    }
  }
}
